import type { ReadonlyDeep } from 'type-fest';
import type { ValidData as DriverReportGenerateDialogFormValidData } from '@/components/dialogForms/DriverReportGenerateDialogForm.vue';
import type { ValidData } from '@/components/forms/LoginForm.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';
import type { JobDraftParams, JobDraftsListItem } from '@/models/draft';

import {
  AxiosError,
  AxiosPromise,
  AxiosRequestConfig,
  AxiosResponse,
  isAxiosError,
  isCancel,
} from 'axios';
import { format } from 'date-fns-tz';

import {
  downloadDataAsCsv,
  downloadDataAsPdf,
  downloadOneBlob,
  downloadResponseAsHeaders,
  getFileDisplayName,
  getFileNamesPreview,
} from '@/utils/file';
import { useUserStore } from '@/stores/useUserStore';
import { bookApiFailureReasons } from '@/helpers/advancedJobBooking';
import * as apiRequestsHelpers from '@/helpers/api/requests';
import * as dateHelpers from '@/helpers/date';
import { getApiErrorText } from '@/helpers/jobEdit';
import {
  showErrorNotification,
  showSuccessNotification,
  showWarningNotification,
} from '@/helpers/notifiers';
import { sendError, sendWarning } from '@/helpers/sentry';
import {
  JobMessageError,
  KDetailedJobError,
  KJobBookingError,
  KJobEditAllocationError,
  KJobEditAllocationLockedError,
  KJobEditAllocationNotFoundError,
  KJobEditAllocationStateChangedError,
  KJobEditAllocationUnlockedError,
  KJobEditLockError,
  KJobEditLockNotFoundError,
  KJobEditLockedByOtherError,
  KJobEditUpdateError,
  KJobEditUpdateLockedError,
  KJobEditUpdateNotFoundError,
  KJobEditUpdateUnlockedError,
  KLoginError,
  SpreadsheetBooking400Error,
} from '@/helpers/throwables';
import { ProductLocation } from '@/models/main';
import { type BookBoxesJobBookApiBody } from '@/models/pacBookBoxesJobBooking';
import { type PacJobBookApiBody } from '@/models/pacJobBooking';
import {
  AmPmJobs,
  ETAReportResponseData,
  ReportRequestParams,
  ReportResponse,
  ReportWeeklyStats,
  RunSheetReportDayData,
  TomorrowReportData,
  TomorrowTobeSchedReportData,
} from '@/models/reports';
import * as api from '@/api';

export const validateOKStatusReqConf = {
  validateStatus: (status: number) => status === 200,
};
export const validateCreatedStatusReqConf = {
  validateStatus: (status: number) => status === 201,
};
export const validateAcceptedStatusReqConf = {
  validateStatus: (status: number) => status === 202,
};
export const validateOKOrNoContentStatusReqConf = {
  validateStatus: (status: number) => status === 200 || status === 204,
};
export const validateNoContentStatusReqConf = {
  validateStatus: (status: number) => status === 204,
};
export const validateOKn404StatusReqConf = {
  validateStatus: (status: number) => {
    return [200, 404].includes(status);
  },
};
export const validateOKn409StatusReqConf = {
  validateStatus: (status: number) => {
    return [200, 409].includes(status);
  },
};

export * from '@/helpers/fetchers/catalog';
export * from '@/helpers/fetchers/sms';
export * from '@/helpers/fetchers/ticket';

function getOnDetailedJobRejected(
  notificationTitle: string,
  notificationText?: string,
) {
  return function onDetailedJobRejected(err: AxiosError) {
    // example `err.response?.status`: 500, 404, 403, 401, 400
    if (isCancel(err)) {
      throw err;
    }

    if (err.response?.status === 400) {
      showErrorNotification({
        title: notificationTitle,
        text: notificationText,
      });
      sendError(err);
      throw new KDetailedJobError(notificationTitle);
    }

    throw err;
  };
}

function getOnReferencesParamsRejected(
  notificationTitle: string,
  notificationText?: string,
) {
  return function onReferencesParamsRejected(err: AxiosError) {
    if (!isCancel(err)) {
      showErrorNotification({
        title: notificationTitle,
        text: notificationText,
        duration: 3000,
      });
      sendError(err);
    }
    throw err;
  };
}

export async function fetchDetailedJob(
  params: {
    jobId: number;
    jobDate?: string;
  },
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.PortalDetailedJobIdContentOpenapi> {
  let resPromise: Promise<AxiosResponse>;
  if (!params.jobDate) {
    resPromise = apiRequestsHelpers.getDashboardJobDetailedByRhId(
      undefined,
      { rh_id: params.jobId },
      { ...validateOKStatusReqConf, ...requestConfig },
    );
  } else {
    resPromise = apiRequestsHelpers.getDashboardJobDetailed(
      {
        job_id: params.jobId,
        job_date: params.jobDate,
      },
      { ...validateOKStatusReqConf, ...requestConfig },
    );
  }
  return await resPromise
    .then((res) => res.data)
    .catch(getOnDetailedJobRejected('Error getting the job'));
}

export async function fetchGetDashboardCustomer(
  params: openapi.CustomerDetailQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.CustomerDetailContentOpenapi> {
  return await apiRequestsHelpers
    .getDashboardCustomer(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch(
      getOnReferencesParamsRejected(
        "Error getting Customer's profile info",
        'Something went wrong while make a request',
      ),
    );
}

export async function fetchGetJobsBookedByUser(
  userTimeZone: string,
  params: {
    user?: string;
    dates?: [Date, Date];
    page?: number;
    pageSize?: number;
  },
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.GetDashboardBookingUserLog_200> {
  return await apiRequestsHelpers
    .getDashboardBookingUserLog(
      {
        user: params.user,
        dates: params.dates
          ? [
              dateHelpers.getApiZonedFormattedDate(
                params.dates[0],
                userTimeZone,
              ),
              dateHelpers.getApiZonedFormattedDate(
                params.dates[1],
                userTimeZone,
              ),
            ].join(',')
          : undefined,
        page: params.page,
        page_size: params.pageSize,
      },
      requestConfig,
    )
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: 'Error getting the jobs' });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetJobsBookedByCustomer(
  userTimeZone: string,
  params: {
    customer?: string;
    dates?: [Date, Date];
    page?: number;
    pageSize?: number;
  },
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.GetDashboardBookingCustomerLog_200> {
  return await apiRequestsHelpers
    .getDashboardBookingCustomerLog(
      {
        customer: params.customer,
        dates: params.dates
          ? [
              dateHelpers.getApiZonedFormattedDate(
                params.dates[0],
                userTimeZone,
              ),
              dateHelpers.getApiZonedFormattedDate(
                params.dates[1],
                userTimeZone,
              ),
            ].join(',')
          : undefined,
        page: params.page,
        page_size: params.pageSize,
      },
      requestConfig,
    )
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: 'Error getting the jobs' });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetJobsBookedLog(
  params: { logId: number },
  requestConfig: AxiosRequestConfig = {},
): Promise<unknown> {
  return await apiRequestsHelpers
    .getDashboardBookingLogOriginalData({ log_id: params.logId }, requestConfig)
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: 'Error getting the log' });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetLookupSuburb(
  params: openapi.AddressBookSuburbLookupListQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.AddressBookSuburbLookupListContentOpenapi> {
  const notificationTitle = 'Error getting the Suburb';
  return await apiRequestsHelpers
    .getLookupSuburb(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetLookupAddress(
  params: openapi.GetLookupAddress_Query,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.GetLookupAddress_200> {
  const notificationTitle = 'Error getting address list';
  try {
    const response = await apiRequestsHelpers.getLookupAddress(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    });
    return response.data;
  } catch (error) {
    if (!isCancel(error)) {
      showErrorNotification({ title: notificationTitle });
      sendError(error);
    }
    throw error;
  }
}

export interface ProductModelLookupItem {
  name: string;
  model_id: number;
  type_code: string;
  weight: number;
  width: number;
  height: number;
  length: number;
}

export async function fetchGetLookupProductModel(
  search: string,
): Promise<ProductModelLookupItem[]> {
  const notificationTitle = 'Error getting the Model';
  return await api
    .getLookupProductModel(search, validateOKStatusReqConf)
    .then((res) => {
      return res.data
        .filter((item): item is typeof item & { name: string } => !!item.name)
        .map((item) => ({
          name: item.name,
          model_id: item.product_id ?? 0,
          type_code: item.type_code ?? '',
          weight: item.weight ?? 0,
          width: item.width ?? 0,
          height: item.height ?? 0,
          length: item.length ?? 0,
        }));
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export interface FinisherModelLookupItem {
  name: string;
  model_id: number;
}

// Generic fallback finisher, always selectable regardless of the search term.
const DEFAULT_FINISHER: FinisherModelLookupItem = {
  name: 'AA-FINISHER',
  model_id: 6182,
};

export async function fetchGetLookupProductFinisher(
  search: string,
  customerCode: string,
): Promise<FinisherModelLookupItem[]> {
  const notificationTitle = 'Error getting the Finisher';
  return await api
    .getLookupProductFinisher(search, customerCode, validateOKStatusReqConf)
    .then((res) => {
      const items = res.data
        .filter(
          (item): item is typeof item & { name: string } =>
            !!item.name && item.name !== DEFAULT_FINISHER.name,
        )
        .map((item) => ({
          name: item.name,
          model_id: item.product_id ?? 0,
        }));
      return [...items, DEFAULT_FINISHER];
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

/**
 * Look up a serialised product's warehouse location/status in Odoo. No toast or
 * Sentry on failure: callers treat any error (including "not found") as out of
 * stock and handle it via their own `try/catch`.
 */
export async function fetchGetProductLocation(
  productId: number,
  serial: string,
  requestConfig: AxiosRequestConfig = {},
): Promise<ProductLocation> {
  return await api
    .getProductLocation(productId, serial, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data);
}

export async function fetchPostDriverStopArrive(
  params: api.PostDriverStopArriveParams,
): Promise<void> {
  try {
    await api.postDriverStopArrive(params, validateCreatedStatusReqConf);
    showSuccessNotification({ text: 'The stop was arrived successfully' });
  } catch (err: unknown) {
    if (!isCancel(err)) {
      showErrorNotification({ title: 'Failed to arrive at the stop' });
      sendError(err);
    }
    throw err;
  }
}

export async function fetchPostDriverStopDepart(
  params: api.PostDriverStopDepartParams,
): Promise<void> {
  try {
    await api.postDriverStopDepart(params, validateCreatedStatusReqConf);
    showSuccessNotification({ text: 'The stop was departed successfully' });
  } catch (err: unknown) {
    if (!isCancel(err)) {
      showErrorNotification({ title: 'Failed to depart the stop' });
      sendError(err);
    }
    throw err;
  }
}

export async function fetchSetJobEditAllocation(
  params: openapi.PostJobeditAllocation_Query,
  data: openapi.PostJobeditAllocation_Body,
  requestConfig: AxiosRequestConfig = {},
): Promise<void> {
  try {
    await apiRequestsHelpers.postJobeditAllocation(params, data, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    });

    switch (data.action) {
      case 'deallocate':
      case 'deallocate_driv':
        showSuccessNotification({ text: 'The deallocation was successful' });
        break;
      case 'preallocate':
        showSuccessNotification({
          text: 'The pre-allocation was successful',
        });
        break;
      case 'allocate':
        showSuccessNotification({ text: 'The allocation was successful' });
        break;
      default:
        showSuccessNotification({
          text: `The "${data.action}" was successful`,
        });
    }
  } catch (err: unknown) {
    if (!isCancel(err)) {
      if (isAxiosError(err)) {
        const errorMessage =
          getApiErrorText(err.response?.data) || 'Error setting allocation';

        switch (err.response?.status) {
          case 400:
            showErrorNotification({
              title: errorMessage,
            });
            sendError(err);
            throw new KJobEditAllocationStateChangedError(errorMessage);
          case 403:
            showErrorNotification({
              title: 'Job is being edited by another user',
            });
            sendError(err);
            throw new KJobEditAllocationLockedError(
              'Job is being edited by another user',
            );
          case 404:
            showErrorNotification({ title: 'Job not found' });
            sendError(err);
            throw new KJobEditAllocationNotFoundError('Job not found');
          case 409:
            showErrorNotification({
              title: 'Job has been unlocked',
            });
            sendError(err);
            throw new KJobEditAllocationUnlockedError('Job has been unlocked');
          default:
            showErrorNotification({
              title: errorMessage,
            });
            sendError(err);
            throw new KJobEditAllocationError(errorMessage);
        }
      }
    }
    throw err;
  }
}

// Set stop and allocation params to drive
// 400 - driver is not allocated
export async function fetchPostJobAllocation(
  params: openapi.JobAllocationCreateBodyOpenapi,
  requestConfig: AxiosRequestConfig = {},
  // `notify: false` suppresses the per-call toasts so a bulk caller can show a
  // single summary instead (errors are still reported to Sentry).
  options: { notify?: boolean } = {},
): Promise<openapi.JobAllocationCreateContentOpenapi> {
  const { notify = true } = options;
  return await apiRequestsHelpers
    .postJobAllocation(undefined, params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => {
      if (notify) {
        showSuccessNotification({
          text: 'The job updated successfully',
        });
      }
      return res.data;
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        if (isAxiosError(err)) {
          if (notify) {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            err.response?.status === 400
              ? showErrorNotification({
                  // TODO it shows even with "Driver code is required for tobase action"
                  text: "You can't split to base stop where driver is not allocated",
                })
              : showErrorNotification({
                  title: 'Error when splitting to base',
                });
          }
          sendError(err);
        }
      }
      throw err;
    });
}

// Runs allocation actions for several jobs and reports a single summary toast,
// instead of one toast per job. Always attempts every job (allSettled).
export async function fetchPostJobAllocationBulk(
  items: openapi.JobAllocationCreateBodyOpenapi[],
  requestConfig: AxiosRequestConfig = {},
): Promise<PromiseSettledResult<openapi.JobAllocationCreateContentOpenapi>[]> {
  const results = await Promise.allSettled(
    items.map((item) =>
      fetchPostJobAllocation(item, requestConfig, { notify: false }),
    ),
  );

  const rejected = results.filter((result) => result.status === 'rejected');
  const failedCount = rejected.length;
  const succeededCount = results.length - failedCount;

  if (succeededCount > 0) {
    showSuccessNotification({
      text:
        failedCount > 0
          ? `${succeededCount} of ${results.length} jobs updated successfully`
          : `${succeededCount} ${succeededCount === 1 ? 'job' : 'jobs'} updated successfully`,
    });
  }
  if (failedCount > 0) {
    // Surface the backend reason from the first failure (e.g. a driver/
    // allocation-rule rejection) instead of a bare count.
    const reason = rejected[0]?.reason;
    const reasonText = isAxiosError(reason)
      ? getApiErrorText(reason.response?.data)
      : undefined;
    showErrorNotification({
      title: `Failed to update ${failedCount} ${failedCount === 1 ? 'job' : 'jobs'}`,
      ...(reasonText ? { text: reasonText } : {}),
    });
  }

  return results;
}

/**
 * Shared `booked`/`failed` response handling for job/PAC book_api: resolves
 * to the booked job's id/date, or throws `KJobBookingError` with per-job
 * reasons (surfaced in the booking error panel, so the notification here is
 * kept to a short title to avoid duplicating them).
 */
async function postJobBookingApi(
  request: AxiosPromise<openapi.PostJobBookApi_201>,
  notificationTitle: string,
): Promise<{ jobId: number; jobDate: string }> {
  return await request
    .then((res) => {
      if (res.data.booked.length > 0) {
        const booked = res.data.booked[0];
        return { jobId: booked.rh_id, jobDate: booked.rh_date };
      }
      const reasons = bookApiFailureReasons(res.data.failed);
      sendError(new Error(notificationTitle), 'postJobBookingApi', res);
      showErrorNotification({ title: notificationTitle });
      throw new KJobBookingError(reasons);
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        if (isAxiosError<openapi.PostJobBookApi_400>(err)) {
          const reasons = bookApiFailureReasons(err.response?.data.failed);
          showErrorNotification({ title: notificationTitle });
          sendError(err);
          throw new KJobBookingError(reasons);
        }
      }
      throw err;
    });
}

export async function fetchPostJobBookApi(
  data: openapi.PostJobBookApi_Body,
  requestConfig: AxiosRequestConfig = {},
): Promise<{ jobId: number; jobDate: string }> {
  return await postJobBookingApi(
    apiRequestsHelpers.postJobBookApi({ dry_run: false }, data, {
      ...validateCreatedStatusReqConf,
      ...requestConfig,
    }),
    'Job booking failed',
  );
}

export async function fetchPostAddressBookUserAddressbook(
  data: openapi.AddressBookUserAddressesPostBodyOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.AddressBookUserAddressesPostContentOpenapi> {
  const notificationTitle = 'Failed to save address to address book';
  return await apiRequestsHelpers
    .postAddressBookUserAddressbook(undefined, data, {
      ...validateCreatedStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetAddressBookAddressbooks(
  params: openapi.AddressBookAddressbooksListQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.AddressBookAddressbooksListContentOpenapi> {
  const notificationTitle = 'Error getting the Address Book';
  return await apiRequestsHelpers
    .getAddressBookAddressbooks(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchPostAddressBookAddresses(
  data: openapi.AddressBookAddressesPostBodyOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.AddressBookAddressesPostContentOpenapi> {
  const notificationTitle = 'Failed to add address';
  return await apiRequestsHelpers
    .postAddressBookAddresses(undefined, data, {
      ...validateCreatedStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchPutAddressBookAddressesById(
  id: number,
  data: openapi.AddressBookAddressesPutBodyOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.AddressBookAddressesPutContentOpenapi> {
  const notificationTitle = 'Failed to update address';
  return await apiRequestsHelpers
    .putAddressBookAddressesById(undefined, { id }, data, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchDeleteAddressBookAddressesById(
  id: number,
  requestConfig: AxiosRequestConfig = {},
): Promise<void> {
  const notificationTitle = 'Failed to delete address';
  await apiRequestsHelpers
    .deleteAddressBookAddressesById(
      undefined,
      { id },
      {
        ...validateNoContentStatusReqConf,
        ...requestConfig,
      },
    )
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchPutAddressBookAddressesByIdMoveTo(
  id: number,
  position: number,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.AddressBookAddressesMovetoContentOpenapi> {
  const notificationTitle = 'Failed to reorder address';
  return await apiRequestsHelpers
    .putAddressBookAddressesByIdMoveTo(
      { position },
      { id },
      {},
      {
        ...validateOKStatusReqConf,
        ...requestConfig,
      },
    )
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchPutAddressBookCustomer(
  addressId: number,
  customerCode: string,
  requestConfig: AxiosRequestConfig = {},
): Promise<void> {
  const notificationTitle = 'Failed to set the default address';
  await apiRequestsHelpers
    .putAddressBookCustomer(
      { address_id: addressId },
      { customer_code: customerCode },
      {
        ...validateOKStatusReqConf,
        ...requestConfig,
      },
    )
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchPostJobDraft(
  data: openapi.PostJobDraft_Body,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.PostJobDraft_201> {
  const notificationTitle = 'Failed to save draft';
  return await apiRequestsHelpers
    .postJobDraft(data, {
      ...validateCreatedStatusReqConf,
      ...requestConfig,
    })
    .then((res) => {
      showSuccessNotification({ title: 'Draft saved' });
      return res.data;
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export interface GetETAReportParams {
  customer: string;
  date: Date;
  page: number;
  page_size: number;
}
export async function fetchGetETAReport(
  userTimeZone: string,
  params: GetETAReportParams,
): Promise<ETAReportResponseData> {
  const notificationTitle = 'Error getting ETA report';
  const apiZonedFormattedDate = dateHelpers.getApiZonedFormattedDate(
    params.date,
    userTimeZone,
  );
  return await api
    .getReportEta(
      {
        ...params,
        date: apiZonedFormattedDate,
      },
      validateOKStatusReqConf,
    )
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        if (isAxiosError(err)) {
          if (err.response?.status === 403) {
            showErrorNotification({
              title: notificationTitle,
              text: 'No access rights to get ETA report',
            });
          } else {
            showErrorNotification({
              title: notificationTitle,
            });
          }
          sendError(err);
        }
      }
      throw err;
    });
}

export interface GetTomorrowReportParams {
  date: string;
  division: string;
}
export async function fetchGetTomorrowScheduledReport(
  params: GetTomorrowReportParams,
): Promise<TomorrowReportData[]> {
  const notificationTitle = 'Error getting Tomorrow Scheduled report';
  return await api
    .getTomorrowReportShedData(params.date, params.division)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        if (isAxiosError(err)) {
          if (err.response?.status === 403) {
            showErrorNotification({
              title: notificationTitle,
              text: 'No access rights to get Tomorrow Scheduled report',
            });
          } else {
            showErrorNotification({
              title: notificationTitle,
            });
          }
          sendError(err);
        }
      }
      throw err;
    });
}

export async function fetchGetTomorrowSplitToBaseReport(
  params: GetTomorrowReportParams,
): Promise<TomorrowReportData[]> {
  const notificationTitle = 'Error getting Tomorrow Split To Base report';
  return await api
    .getTomorrowReportSplitData(params.date, params.division)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        if (isAxiosError(err)) {
          if (err.response?.status === 403) {
            showErrorNotification({
              title: notificationTitle,
              text: 'No access rights to get Tomorrow Split To Base report',
            });
          } else {
            showErrorNotification({
              title: notificationTitle,
            });
          }
          sendError(err);
        }
      }
      throw err;
    });
}

export async function fetchGetTomorrowToBeScheduledReport(
  params: GetTomorrowReportParams,
): Promise<TomorrowTobeSchedReportData[]> {
  const notificationTitle = 'Error getting Tomorrow To be Scheduled report';
  return await api
    .getTomorrowReportTobeSchedData(params.date, params.division)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        if (isAxiosError(err)) {
          if (err.response?.status === 403) {
            showErrorNotification({
              title: notificationTitle,
              text: 'No access rights to get Tomorrow To be Scheduled report',
            });
          } else {
            showErrorNotification({
              title: notificationTitle,
            });
          }
          sendError(err);
        }
      }
      throw err;
    });
}

export async function fetchGetTomorrowScheduledReportFile(
  params: GetTomorrowReportParams,
): Promise<void> {
  const notificationTitle = 'Error getting Tomorrow Scheduled report';
  const { division, date } = params;
  await api
    .getTomorrowReportSchedPDF(date, division)
    .then((res) => {
      const stem = division + '_' + date + '_report_tomorrow';
      downloadDataAsPdf(res.data, stem);
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetTomorrowToBeScheduledReportFile(
  params: GetTomorrowReportParams,
): Promise<void> {
  const notificationTitle = 'Error getting Tomorrow To Be Scheduled report';
  const { division, date } = params;
  await api
    .getTomorrowReportTobeSchedPDF(date, division)
    .then((res) => {
      const stem = division + '_' + date + '_report_tomorrow';
      downloadDataAsPdf(res.data, stem);
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetTomorrowSplitToBaseReportFile(
  params: GetTomorrowReportParams,
): Promise<void> {
  const notificationTitle = 'Error getting Tomorrow Split To Base report';
  const { division, date } = params;
  await api
    .getTomorrowReportFile(date, division)
    .then((res) => {
      const stem = division + '_' + date + '_report_tomorrow';
      downloadDataAsPdf(res.data, stem);
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export interface SendETAReportParams {
  customer: string;
  date: Date;
}
export async function fetchSendETAReport(
  userTimeZone: string,
  params: SendETAReportParams,
): Promise<void> {
  const apiZonedFormattedDate = dateHelpers.getApiZonedFormattedDate(
    params.date,
    userTimeZone,
  );
  await api
    .postReportEta(
      {
        ...params,
        date: apiZonedFormattedDate,
      },
      validateOKStatusReqConf,
    )
    .then(() => {
      showSuccessNotification({
        text: 'The report was successfully sent',
      });
    })
    .catch((err: unknown) => {
      if (isCancel(err)) throw err;

      showErrorNotification({
        title: 'Error sending the report',
      });
      sendError(err);
      throw err;
    });
}

export interface GetReportEtaFileParams {
  customer: string;
  date: Date;
  type: 'PDF' | 'CSV';
}
export async function fetchGetReportEtaFile(
  userTimeZone: string,
  params: GetReportEtaFileParams,
): Promise<void> {
  const notificationTitle = 'Error getting ETA report';
  const { customer, type } = params;
  const apiZonedFormattedDate = dateHelpers.getApiZonedFormattedDate(
    params.date,
    userTimeZone,
  );
  await api
    .getReportEtaFile({ customer, date: apiZonedFormattedDate, type })
    .then((res) => {
      const stem = customer + '_' + apiZonedFormattedDate + '_report_eta';
      if (type === 'PDF') {
        downloadDataAsPdf(res.data, stem);
      } else {
        downloadDataAsCsv(res.data, stem);
      }
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetJobPrintoutFile(
  params: openapi.JobPrintoutListQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<void> {
  const notificationTitle = 'Error downloading the pdf';
  await apiRequestsHelpers
    .getJobPrintoutFile(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => {
      downloadResponseAsHeaders(
        res,
        `Shipping label ${params.job_id}_${params.job_date}`,
      );
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetUserProfile(
  params?: openapi.UserProfileReadQueryOpenapi,
): Promise<openapi.UserProfileReadContentOpenapi> {
  const notificationTitle = 'Having problems getting the profile';
  return await api
    .getUserProfile(params, validateOKStatusReqConf)
    .then((res) => {
      // TODO
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!res.data) throw new Error('no userData');
      return res.data;
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetJobDraft(
  params: openapi.JobDraftListQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.JobDraftListContentOpenapi> {
  const notificationTitle = 'Error loading drafts';
  return await apiRequestsHelpers
    .getJobDraft(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetJobDraftById(
  id: number,
  requestConfig: AxiosRequestConfig = {},
): Promise<JobDraftsListItem> {
  return await api.getDraft(id, requestConfig).catch((err: unknown) => {
    if (!isCancel(err)) {
      showErrorNotification({ title: 'Error loading the draft' });
      sendError(err);
    }
    throw err;
  });
}

export async function fetchPutJobDraft(
  id: number,
  data: JobDraftParams,
): Promise<void> {
  const notificationTitle = 'Failed to update draft';
  await api
    .editDraft(id, data, validateOKStatusReqConf)
    .then(() => {
      showSuccessNotification({ title: 'Draft updated' });
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchDeleteDraft(id: number): Promise<void> {
  const notificationTitle = 'Error deleting the draft';
  return await api
    .deleteDraft(id, validateNoContentStatusReqConf)
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetReportFile(
  reportId: number,
  reportType: string,
): Promise<void> {
  const notificationTitle = `Error getting ${reportType} report`;
  await api
    .getReportFile(reportId)
    .then((res) => {
      downloadResponseAsHeaders(res, 'kkts_report');
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetKpiReport(
  customer: string,
): Promise<ReportResponse[]> {
  const notificationTitle = 'Error getting KPI report';
  const params: ReportRequestParams = {
    // month: '2023-09-01',
    customer_codes: customer,
    report_type: 2,
  };
  return await api
    .getReports(params, validateOKStatusReqConf)
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export interface PostReportsGenerateKpiReportParams {
  date: string;
  customer: string;
}
export async function fetchPostReportsGenerateKpiReport(
  reportParams: PostReportsGenerateKpiReportParams,
): Promise<string> {
  const params: ReportRequestParams = {
    month: reportParams.date,
    customer_codes: reportParams.customer,
    report_type: 2,
  };
  return await api
    .postReportsGenerateReport(params, validateAcceptedStatusReqConf)
    .then((res) => {
      showSuccessNotification({
        title: 'KPI report requested',
        text: 'Wait for the result. It could take up to 10-15m in case of big Report.',
      });
      return res.data;
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: 'Error generation KPI report' });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetDriverReport(
  userTimeZone: string,
  createdAt: Date,
): Promise<ReportResponse[]> {
  const notificationTitle = 'Error getting Driver report';
  const params: ReportRequestParams = {
    created_at: dateHelpers.getApiZonedFormattedDate(createdAt, userTimeZone),
    report_type: 1,
  };
  return await api
    .getReports(params, validateOKStatusReqConf)
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchPostReportsGenerateDriverReport(
  reportParams: DriverReportGenerateDialogFormValidData,
): Promise<string> {
  const params: ReportRequestParams = {
    report_type: 1,
  };
  if (reportParams.rangeType === 'week') {
    params.week = format(reportParams.weekRange[0], 'yyyy-MM-dd');
  } else {
    params.month = reportParams.monthDate;
  }
  return await api
    .postReportsGenerateReport(params, validateAcceptedStatusReqConf)
    .then((res) => {
      showSuccessNotification({
        title: 'Driver report requested',
        text: 'Wait for the result. It could take up to 10-15m in case of big Report.',
      });
      return res.data;
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: 'Error generation Driver report' });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetAmPmJobs(
  userTimeZone: string,
  date: Date,
): Promise<AmPmJobs> {
  const notificationTitle = 'Error getting AM / PM report';
  const apiZonedFormattedDate = dateHelpers.getApiZonedFormattedDate(
    date,
    userTimeZone,
  );
  return await api
    .getAmPmJobs(apiZonedFormattedDate, validateOKStatusReqConf)
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchLogIn(formData: ValidData): Promise<void> {
  const notificationTitle = 'Username or password invalid';
  try {
    await api.logIn(formData);
  } catch (err: unknown) {
    if (isCancel(err)) throw err;

    showErrorNotification({ title: '', text: notificationTitle });
    sendError(err);
    throw new KLoginError(notificationTitle);
  }
}

export async function fetchGetReportWeeklyStat(
  userTimeZone: string,
  params: {
    states: string[];
    dates: [Date, Date];
  },
): Promise<ReportWeeklyStats> {
  const notificationTitle = 'Error getting Weekly report';
  const apiZonedFormattedDates = [
    dateHelpers.getApiZonedFormattedDate(params.dates[0], userTimeZone),
    dateHelpers.getApiZonedFormattedDate(params.dates[1], userTimeZone),
  ];
  return await api
    .getReportWeeklyStat(
      {
        states: params.states,
        dates: apiZonedFormattedDates,
      },
      validateOKStatusReqConf,
    )
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetRunSheetReport(
  userTimeZone: string,
  params: {
    states: string[];
    dates: [Date, Date];
  },
): Promise<RunSheetReportDayData> {
  const notificationTitle = 'Error getting Run Sheet report';
  const apiZonedFormattedDates = [
    dateHelpers.getApiZonedFormattedDate(params.dates[0], userTimeZone),
    dateHelpers.getApiZonedFormattedDate(params.dates[1], userTimeZone),
  ];
  return await api
    .getRunSheetList(
      {
        states: params.states,
        dates: apiZonedFormattedDates,
      },
      validateOKStatusReqConf,
    )
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetRunSheetReportFile(
  params: [string, number][],
): Promise<void> {
  const notificationTitle = 'Error getting Run Sheet report';
  await api
    .getRunSheetPDFs(params)
    .then((res) => {
      const stem = 'report_run_sheet';
      downloadDataAsPdf(res.data, stem);
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

const noReturnLabelsNotification = {
  title: 'No Return labels',
  text: 'The selected drivers have no products to return',
};

export async function fetchPostReportReturnLabelsFile(
  params: {
    drivers: [string, number][];
    size: openapi.PostReportReturnLabels_Body['size'];
    format: openapi.PostReportReturnLabels_Body['format'];
  },
  requestConfig: AxiosRequestConfig = {},
): Promise<void> {
  const notificationTitle = 'Error getting Return labels';
  await apiRequestsHelpers
    .postReportReturnLabelsFile(
      {
        drivers: params.drivers,
        size: params.size,
        format: params.format,
      },
      { ...validateOKOrNoContentStatusReqConf, ...requestConfig },
    )
    .then((res) => {
      if (res.status === 204) {
        showWarningNotification(noReturnLabelsNotification);
        return;
      }
      const stem = 'return_labels';
      if (params.format === 'zpl') {
        downloadOneBlob(res.data, 'application/octet-stream', stem + '.zpl');
        return;
      }
      downloadDataAsPdf(res.data, stem);
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchPostReportReturnLabelsPrint(
  params: {
    drivers: [string, number][];
    size: openapi.PostReportReturnLabelsPrint_Body['size'];
    printerId: number;
    copies: number;
  },
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.PostReportReturnLabelsPrint_200 | undefined> {
  const notificationTitle = 'Print error';
  return await apiRequestsHelpers
    .postReportReturnLabelsPrint(
      {
        drivers: params.drivers,
        size: params.size,
        printer_id: params.printerId,
        copies: params.copies,
      },
      { ...validateOKOrNoContentStatusReqConf, ...requestConfig },
    )
    .then((res) => {
      if (res.status === 204) {
        showWarningNotification(noReturnLabelsNotification);
        return undefined;
      }
      const labels = res.data.labels;
      showSuccessNotification({
        text: `${labels} ${labels === 1 ? 'label' : 'labels'} sent to the printer`,
      });
      return res.data;
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetDashboardTracking(
  userTimeZone: string,
  date: Date,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.DashboardJobsTrackingContentOpenapi> {
  const notificationTitle = 'Error getting data';
  const apiZonedFormattedDate = dateHelpers.getApiZonedFormattedDate(
    date,
    userTimeZone,
  );
  return await apiRequestsHelpers
    .getDashboardTracking(
      { date: apiZonedFormattedDate },
      { ...validateOKStatusReqConf, ...requestConfig },
    )
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetLookupCustomer(
  params: openapi.LookupCustomerListQueryOpenapi,
): Promise<string[]> {
  const notificationTitle = 'Error getting customer codes';
  return await api
    .getLookupCustomer(params, validateOKStatusReqConf)
    .then((res) => {
      if (typeof res.data === 'string') return [res.data];
      return res.data;
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetLookupDriverByJob(
  params: openapi.LookupDriverByJobOpenapi,
): Promise<string[]> {
  const notificationTitle = 'Error getting drivers';
  return await api
    .getLookupDriverByJob(params, validateOKStatusReqConf)
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetLookupService(
  params: openapi.LookupServiceListOpenapi,
): Promise<string[]> {
  const notificationTitle = 'Error getting service codes';
  return await api
    .getLookupService(params, validateOKStatusReqConf)
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetJobSearch(
  userTimeZone: string,
  params: {
    dates: [Date, Date];
    jobIds?: number[];
    filters?: string[];
    states?: string[];
    customerCodes?: string[];
    serviceCodes?: string[];
    driverIds?: string[] | number[];
    serialNumber?: string;
    stopAddress?: string;
    combinedData?: string;
    payOnHold?: boolean;
  },
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.GetJobSearch_1_200> {
  const notificationTitle = 'Error getting jobs';
  const apiZonedFormattedDates = [
    dateHelpers.getApiZonedFormattedDate(params.dates[0], userTimeZone),
    dateHelpers.getApiZonedFormattedDate(params.dates[1], userTimeZone),
  ];
  return await apiRequestsHelpers
    .getJobSearch(
      {
        dates: apiZonedFormattedDates.join(','),
        job_id: params.jobIds?.join(','),
        service_code: params.serviceCodes?.join(','),
        driver_id: params.driverIds?.join(','),
        customer_code: params.customerCodes?.join(','),
        serial_number: params.serialNumber,
        state: params.states?.join(','),
        mixed_content: params.combinedData,
        mixed_address: params.stopAddress,
        extra: params.filters?.join(
          ',',
        ) as openapi.GetJobSearch_1_Query['extra'],
        pay_on_hold: params.payOnHold,
      },
      { ...validateOKStatusReqConf, ...requestConfig },
    )
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetDashboardSearchPdf(
  userTimeZone: string,
  params: {
    dates: [Date, Date];
    jobIds?: number[];
    filters?: string[];
    states?: string[];
    customerCodes?: string[];
    serviceCodes?: string[];
    driverIds?: string[] | number[];
    serialNumber?: string;
    stopAddress?: string;
    combinedData?: string;
    payOnHold?: boolean;
  },
  requestConfig: AxiosRequestConfig = {},
): Promise<void> {
  const notificationTitle = 'Error downloading PDF';
  const apiZonedFormattedDates = [
    dateHelpers.getApiZonedFormattedDate(params.dates[0], userTimeZone),
    dateHelpers.getApiZonedFormattedDate(params.dates[1], userTimeZone),
  ];
  await apiRequestsHelpers
    .getDashboardSearchPdf(
      {
        dates: apiZonedFormattedDates.join(','),
        job_id: params.jobIds?.join(','),
        service_code: params.serviceCodes?.join(','),
        driver_id: params.driverIds?.join(','),
        customer_code: params.customerCodes?.join(','),
        serial_number: params.serialNumber,
        state: params.states?.join(','),
        mixed_content: params.combinedData,
        mixed_address: params.stopAddress,
        extra: params.filters?.join(
          ',',
        ) as openapi.GetDashboardSearchPdf_Query['extra'],
        pay_on_hold: params.payOnHold,
      },
      { ...validateOKStatusReqConf, ...requestConfig },
    )
    .then((res) => {
      const stem = 'advancedSearch';
      downloadDataAsPdf(res.data, stem);
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetJobSearchCount(
  userTimeZone: string,
  params: {
    dates: [Date, Date];
    jobIds?: number[];
    filters?: string[];
    states?: string[];
    customerCodes?: string[];
    serviceCodes?: string[];
    driverIds?: string[] | number[];
    serialNumber?: string;
    stopAddress?: string;
    combinedData?: string;
    payOnHold?: boolean;
    count: boolean;
  },
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.GetJobSearch_2_200> {
  const notificationTitle = 'Error getting jobs count';
  const apiZonedFormattedDates = [
    dateHelpers.getApiZonedFormattedDate(params.dates[0], userTimeZone),
    dateHelpers.getApiZonedFormattedDate(params.dates[1], userTimeZone),
  ];
  return await apiRequestsHelpers
    .getJobSearchCount(
      {
        dates: apiZonedFormattedDates.join(','),
        job_id: params.jobIds?.join(','),
        service_code: params.serviceCodes?.join(','),
        driver_id: params.driverIds?.join(','),
        customer_code: params.customerCodes?.join(','),
        serial_number: params.serialNumber,
        state: params.states?.join(','),
        mixed_content: params.combinedData,
        mixed_address: params.stopAddress,
        extra: params.filters?.join(
          ',',
        ) as openapi.GetJobSearch_2_Query['extra'],
        pay_on_hold: params.payOnHold,
        count: params.count,
      },
      { ...validateOKStatusReqConf, ...requestConfig },
    )
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetLookupSuburbGnaf(
  params: {
    suburbGuess?: string;
    postCode?: number;
  },
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.AddressBookGnafSuburbLookupListContentOpenapi> {
  const notificationTitle = 'Error getting suburbs';
  return await apiRequestsHelpers
    .getLookupSuburbGnaf(
      {
        suburb_guess: params.suburbGuess,
        postcode: params.postCode?.toString(),
      },
      {
        ...validateOKStatusReqConf,
        ...requestConfig,
      },
    )
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetJobSearchByPostcode(
  userTimeZone: string,
  params: {
    dates: [Date, Date];
    suburbId: number;
    postCode: number;
    distance: number;
  },
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.JobSearchJobByPostcodeContentOpenapi> {
  const notificationTitle = 'Error getting jobs';
  const apiZonedFormattedDates = [
    dateHelpers.getApiZonedFormattedDate(params.dates[0], userTimeZone),
    dateHelpers.getApiZonedFormattedDate(params.dates[1], userTimeZone),
  ];
  return await apiRequestsHelpers
    .getJobSearchByPostcode(
      {
        dates: apiZonedFormattedDates.join(','),
        suburb_id: params.suburbId,
        postcode: params.postCode,
        distance: params.distance,
      },
      {
        ...validateOKStatusReqConf,
        ...requestConfig,
      },
    )
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchProfileUpdate(
  formData: FormData,
): Promise<openapi.UserProfileUpdateContentOpenapi> {
  const notificationErrorText = 'Error updating the profile';
  const notificationSuccessText = 'Profile updated';
  const userStore = useUserStore();
  return await api
    .putUserProfile(undefined, formData, validateOKStatusReqConf)
    .then((res) => {
      showSuccessNotification({ text: notificationSuccessText });
      userStore.setUser(res.data);
      return res.data;
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: '', text: notificationErrorText });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetNews(
  params?: openapi.NewsListQueryOpenapi,
): Promise<openapi.NewsListContentOpenapi> {
  const notificationTitle = 'Error loading News data';
  return await api
    .getNews(params, validateOKStatusReqConf)
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetFilters(
  params: openapi.GetFilters_Query,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.GetFilters_200> {
  const notificationTitle = 'Error when getting the list of filters';
  return await api
    .getFilters(params, { ...validateOKStatusReqConf, ...requestConfig })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendWarning(err, notificationTitle);
      }
      throw err;
    });
}

export async function fetchPostFilters(
  data: openapi.PostFilters_Body,
): Promise<openapi.PostFilters_201> {
  return await api
    .postFilters(undefined, data, validateCreatedStatusReqConf)
    .then((res) => {
      showSuccessNotification({ text: 'The filter was created successfully' });
      return res.data;
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: 'Failed to create the filter' });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchDeleteFilter(
  id: number,
): Promise<openapi.DeleteFiltersById_204> {
  const notificationTitle = 'Error deleting the filter';
  return await api
    .deleteFiltersById(
      undefined,
      { id: id.toString() },
      validateNoContentStatusReqConf,
    )
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetHelpcenterFaq(
  params?: openapi.ListFAQQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.ListFAQContentOpenapi> {
  const notificationTitle =
    'Error getting the list of frequently asked questions';
  return await apiRequestsHelpers
    .getHelpcenterFaq(params, {
      ...validateOKn404StatusReqConf,
      ...requestConfig,
    })
    .then((res) => {
      if (res.status === 404) return [];
      return res.data;
    })
    .catch((err: unknown) => {
      if (isCancel(err)) throw err;

      showErrorNotification({ title: notificationTitle });
      sendError(err);
      throw err;
    });
}

export async function fetchGetHelpcenterTopSearches(
  params?: openapi.ListTopSearchesQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.ListTopSearchesContentOpenapi> {
  const notificationTitle = 'Error getting the list of top searched articles';
  return await apiRequestsHelpers
    .getHelpcenterTopSearches(params, {
      ...validateOKn404StatusReqConf,
      ...requestConfig,
    })
    .then((res) => {
      if (res.status === 404) return [];
      return res.data;
    })
    .catch((err: unknown) => {
      if (isCancel(err)) throw err;

      showErrorNotification({ title: notificationTitle });
      sendError(err);
      throw err;
    });
}

export async function fetchGetHelpcenterRecentUpdates(
  params?: openapi.ListRecentUpdatesQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.ListRecentUpdatesContentOpenapi> {
  const notificationTitle =
    'Error getting the list of recently updated articles';
  return await apiRequestsHelpers
    .getHelpcenterRecentUpdates(params, {
      ...validateOKn404StatusReqConf,
      ...requestConfig,
    })
    .then((res) => {
      if (res.status === 404) return [];
      return res.data;
    })
    .catch((err: unknown) => {
      if (isCancel(err)) throw err;

      showErrorNotification({ title: notificationTitle });
      sendError(err);
      throw err;
    });
}

export async function fetchGetHelpcenterArticleSearch(
  params: openapi.SearchArticlesQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.SearchArticlesContentOpenapi> {
  const notificationTitle = 'Error getting the list of articles';
  return await apiRequestsHelpers
    .getHelpcenterArticleSearch(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetHelpcenterArticleRoute(
  params: openapi.GetArticlesByRouteQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.GetArticlesByRouteContentOpenapi | []> {
  const notificationTitle = 'Error getting the list of articles by route';
  return await apiRequestsHelpers
    .getHelpcenterArticleRoute(params, {
      ...validateOKn404StatusReqConf,
      ...requestConfig,
    })
    .then((res) => {
      // "Not found" is ok, and we just return empty array
      if (res.status === 404) {
        return [];
      } else {
        return res.data;
      }
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetHelpcenterArticleById(
  path: openapi.GetArticleByIdPathOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.GetArticleByIdContentOpenapi> {
  const notificationTitle = 'Error getting the article';
  return await apiRequestsHelpers
    .getHelpcenterArticleById(undefined, path, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchPostHelpcenterArticleByIdUpvote(
  path: openapi.UpvoteArticlePathOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.UpvoteArticleContentOpenapi> {
  const notificationTitle = 'Error upvote the article';
  return await apiRequestsHelpers
    .postHelpcenterArticleByIdUpvote(undefined, path, undefined, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchPostHelpcenterArticleByIdDownvote(
  path: openapi.UpvoteArticlePathOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.UpvoteArticleContentOpenapi> {
  const notificationTitle = 'Error downvote the article';
  return await apiRequestsHelpers
    .postHelpcenterArticleByIdDownvote(undefined, path, undefined, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetJobTicketsSearchByJob(
  params: openapi.JobGetTicketsByJobShortQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.JobGetTicketsByJobShortContentOpenapi> {
  const notificationTitle = 'Error getting mail tickets';
  return await apiRequestsHelpers
    .getJobTicketsSearchByJob(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchPostJobMessageVisibility(
  data: openapi.JobMessageVisibleUpdateBodyOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.JobMessageVisibleUpdateContentOpenapi> {
  const notificationTitle = 'Error changing the message visibility';
  return await apiRequestsHelpers
    .postJobMessageVisibility(undefined, data, {
      ...validateCreatedStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetJobAirroad(
  params: openapi.JobGetAirroadQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.JobGetAirroadContentOpenapi> {
  const notificationTitle = 'Error getting AirRoad detailed data';
  return await apiRequestsHelpers
    .getJobAirroad(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetJobStopImage(
  params: openapi.GetStopImagesQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.GetStopImagesContentOpenapi> {
  const notificationTitle = 'Error getting stop images';
  return await apiRequestsHelpers
    .getJobStopImage(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchPostAirroadJobsDownloadLabelsFile(
  connote: string,
  data: openapi.AirroadDownloadLabelsBodyOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<void> {
  const notificationTitle = 'Error downloading labels';
  await apiRequestsHelpers
    .postAirroadJobsDownloadLabelsFile(undefined, data, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => {
      const stem = connote + '_labels';
      downloadDataAsPdf(res.data, stem);
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchDeleteJobAttachmentById(
  attachmentId: number,
): Promise<openapi.PortalJobDeleteAttachContentOpenapi> {
  const notificationTitle = 'Error deleting the attachment';
  return await apiRequestsHelpers
    .deleteJobAttachmentById(
      undefined,
      { id: attachmentId },
      validateNoContentStatusReqConf,
    )
    .then((res) => {
      showSuccessNotification({
        text: 'The attachment successfully deleted',
      });
      return res.data;
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchPostJobStopImages(data: {
  job_id: number;
  job_date: string;
  images: File[];
  stop_number: number;
  ref: string;
}): Promise<openapi.DownloadStopImagesContentOpenapi[]> {
  const notificationTitle = 'Error uploading the photo';
  const uploadedImages: openapi.DownloadStopImagesContentOpenapi[] = [];
  const failedFileNames: string[] = [];
  const totalImages = data.images.length;

  for (const [index, image] of data.images.entries()) {
    const fileName = getFileDisplayName(image, index, 'image');

    try {
      const response = await apiRequestsHelpers.postJobStopImage(
        undefined,
        {
          job_id: data.job_id,
          job_date: data.job_date,
          image,
          stop_number: data.stop_number,
          ref: data.ref,
        },
        validateCreatedStatusReqConf,
      );
      uploadedImages.push(response.data);
    } catch {
      failedFileNames.push(fileName);
    }
  }

  if (failedFileNames.length === 0) {
    showSuccessNotification({
      text: `${uploadedImages.length} ${uploadedImages.length === 1 ? 'photo' : 'photos'} successfully uploaded`,
    });
    return uploadedImages;
  }

  const failedPreview = getFileNamesPreview(failedFileNames);

  if (uploadedImages.length === 0) {
    showErrorNotification({
      title: notificationTitle,
      text: `${failedFileNames.length} ${failedFileNames.length === 1 ? 'photo' : 'photos'} failed to upload: ${failedPreview}`,
    });
    throw new Error(notificationTitle);
  }

  showWarningNotification({
    title: 'Partial upload completed',
    text: `Uploaded ${uploadedImages.length}/${totalImages} photos. Failed: ${failedPreview}`,
  });

  return uploadedImages;
}

export async function fetchDeleteJobStopImage(
  params: openapi.DeleteJobStopImage_Query,
): Promise<void> {
  const notificationTitle = 'Error deleting the photo';
  return await apiRequestsHelpers
    .deleteJobStopImage(params, validateNoContentStatusReqConf)
    .then((res) => {
      showSuccessNotification({
        text: 'Photo successfully deleted',
      });
      return res.data;
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchPostJobAttachment(
  data: {
    rh_id: number;
    rh_date: string;
    files: File[];
  },
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.PortalJobAttachContentOpenapi> {
  const notificationTitle = 'Attachments upload failed';
  return await apiRequestsHelpers
    .postJobAttachment(undefined, data, {
      ...validateCreatedStatusReqConf,
      ...requestConfig,
    })
    .then((res) => {
      showSuccessNotification({
        text: 'Attachments successfully uploaded',
      });
      return res.data;
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchPostJobBookXls(
  params: openapi.PostJobBookXls_Query,
  // data: openapi.PostJobBookXls_Body, // TODO
  file: File,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.PostJobBookXls_201> {
  return await apiRequestsHelpers
    .postJobBookXls(params, file, {
      ...validateCreatedStatusReqConf,
      ...requestConfig,
    })
    .then((res) => {
      return res.data;
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        if (isAxiosError(err)) {
          switch (err.response?.status) {
            case 400:
              sendWarning(err);
              throw new SpreadsheetBooking400Error('400', { cause: err });
            case 500:
              showErrorNotification({
                title: 'Error HTTP 500. Something wrong with the file',
              });
              sendError(err);
              break;
            default:
              showErrorNotification({
                title: 'Unknown error during uploading the spreadsheet',
              });
              sendError(err);
              break;
          }
        }
      }
      throw err;
    });
}

export async function fetchGetJobHistory(
  params: openapi.JobHistoryQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.JobHistoryContentOpenapi> {
  const notificationTitle = 'Error getting job history';
  return await apiRequestsHelpers
    .getJobHistory(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetUserPrinterOptions(
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.UserPrinterOptionsContentOpenapi> {
  const notificationTitle = 'Error getting user printers';
  return await apiRequestsHelpers
    .getUserPrinterOptions(undefined, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchPostAirroadJobsByIdPrintLabel(
  params: {
    arJobId: number;
    printerId: number;
  },
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.AirroadPrintLabelContentOpenapi> {
  const notificationTitle = 'Print error';
  return await apiRequestsHelpers
    .postAirroadJobsByIdPrintLabel(
      undefined,
      { id: params.arJobId },
      { printer_id: params.printerId },
      {
        ...validateOKStatusReqConf,
        ...requestConfig,
      },
    )
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchPostJobMessage(
  data: openapi.DriverSendMessageCreateBodyOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.DriverSendMessageCreateContentOpenapi> {
  const notificationTitle = 'Error sending the message';
  return await apiRequestsHelpers
    .postJobMessage(undefined, data, {
      ...validateCreatedStatusReqConf,
      ...requestConfig,
    })
    .then((res) => {
      if (res.data.Result === 'Message sent') {
        showSuccessNotification({ text: 'The message was sent successfully' });
        return res.data;
      } else {
        throw new JobMessageError("Message doesn't sent");
      }
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchPostZammadTickets(
  data: openapi.JobCreateTicketBodyOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.JobCreateTicketContentOpenapi> {
  const notificationTitle = 'An error occurred when creating the ticket';
  return await apiRequestsHelpers
    .postZammadTickets(undefined, data, {
      ...validateCreatedStatusReqConf,
      ...requestConfig,
    })
    .then((res) => {
      showSuccessNotification({ text: 'The ticket was successfully created' });
      return res.data;
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetZammadChannelsEmails(
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.ZammadChannelsEmailsContentOpenapi> {
  const notificationTitle = 'Error getting emails list';
  return await apiRequestsHelpers
    .getZammadChannelsEmails(undefined, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetZammadLookupEmail(
  params: openapi.ZammadLookupEmailQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.ZammadLookupEmailContentOpenapi> {
  const notificationTitle = 'Error getting emails';
  return await apiRequestsHelpers
    .getZammadLookupEmail(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchPutJobCanSchedule(
  data: openapi.JobCanscheduleCreateBodyOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.JobCanscheduleCreateContentOpenapi> {
  const notificationTitle = 'Error changing the schedule status';
  return await apiRequestsHelpers
    .putJobCanSchedule(undefined, data, {
      ...validateOKn409StatusReqConf,
      ...requestConfig,
    })
    .then((res) => {
      if (res.status === 200) {
        showSuccessNotification({
          text: 'Successfully changed the "Can schedule" status',
        });
      } else if (res.status === 409) {
        // We update the status but can't update RH Dispatch due to the lock.
        showSuccessNotification({
          text: 'Successfully changed the "Can schedule" status but can\'t update RH Dispatch',
        });
      }
      return res.data;
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchPostJobFutile(
  data: openapi.JobFutileCreateBodyOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.JobFutileCreateContentOpenapi> {
  return await apiRequestsHelpers
    .postJobFutile(undefined, data, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => {
      showSuccessNotification({
        text: 'The job was successfully closed as futile',
      });
      return res.data;
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        if (isAxiosError(err)) {
          if (
            err.response?.status === 400 &&
            err.response.data.non_field_errors[0] === 'Not allocated'
          ) {
            showErrorNotification({ text: 'The job is not allocated' });
          } else {
            showErrorNotification({
              title: 'Error when closing a job as futile',
            });
          }
          sendError(err);
        }
      }
      throw err;
    });
}

export async function fetchPostJobCancel(
  data: openapi.JobCancelBodyOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.JobCancelContentOpenapi> {
  return await apiRequestsHelpers
    .postJobCancel(undefined, data, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => {
      showSuccessNotification({ text: 'The job was successfully canceled' });
      return res.data;
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: 'Error when canceling a job' });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchPostJobSetOnHold(
  params: openapi.JobSetOnHoldQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.JobSetOnHoldContentOpenapi> {
  return await apiRequestsHelpers
    .postJobSetOnHold(params, {
      ...validateCreatedStatusReqConf,
      ...requestConfig,
    })
    .then((res) => {
      showSuccessNotification({ text: 'Job set on hold' });
      return res.data;
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        if (isAxiosError(err)) {
          const errorText = getApiErrorText(err.response?.data);
          showErrorNotification({
            title: errorText || 'Failed to set job on hold',
          });
        } else {
          showErrorNotification({ title: 'Failed to set job on hold' });
        }
        sendError(err);
      }
      throw err;
    });
}

export async function fetchPostJobUnsetOnHold(
  params: openapi.JobUnsetOnHoldQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.JobUnsetOnHoldContentOpenapi> {
  return await apiRequestsHelpers
    .postJobUnsetOnHold(params, {
      ...validateCreatedStatusReqConf,
      ...requestConfig,
    })
    .then((res) => {
      showSuccessNotification({ text: 'Job set as unhold' });
      return res.data;
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        if (isAxiosError(err)) {
          const errorText = getApiErrorText(err.response?.data);
          showErrorNotification({
            title: errorText || 'Failed to set job as unhold',
          });
        } else {
          showErrorNotification({ title: 'Failed to set job as unhold' });
        }
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetJobPriceSc(
  params: openapi.GetJobPriceSc_Query,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.GetJobPriceSc_200> {
  const notificationTitle = 'Error calculating service code price';
  return await apiRequestsHelpers
    .getJobPriceSc(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetJobResync(
  params: openapi.JobResyncQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.JobResyncContentOpenapi> {
  const notificationTitle = 'Error sending the request';
  return await apiRequestsHelpers
    .getJobResync(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => {
      showSuccessNotification({ text: 'Job re-synced from the RH' });
      return res.data;
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetJobDetailedOrGetJobDetailedByRhId(
  params: {
    jobId: number;
    jobDate?: string;
  },
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.JobDetailedOpenapi> {
  let resPromise: Promise<AxiosResponse>;
  if (!params.jobDate) {
    resPromise = apiRequestsHelpers.getJobDetailedByRhId(
      undefined,
      { rh_id: params.jobId },
      { ...validateOKStatusReqConf, ...requestConfig },
    );
  } else {
    resPromise = apiRequestsHelpers.getJobDetailed(
      {
        job_id: params.jobId,
        job_date: params.jobDate,
      },
      { ...validateOKStatusReqConf, ...requestConfig },
    );
  }
  return await resPromise
    .then((res) => res.data)
    .catch(getOnDetailedJobRejected('Error getting the detailed job'));
}

export async function fetchGetAirroadJobDetailed(
  params: openapi.AirroadJobDetailedQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.AirroadJobDetailedContentOpenapi> {
  const notificationTitle = 'Error getting single job for Airroad booking';
  return await apiRequestsHelpers
    .getAirroadJobDetailed(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetAddressBookAddresses(
  params: openapi.AddressBookAddressesListQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.AddressBookAddressesListContentOpenapi> {
  const notificationTitle = 'Error getting the Address Book';
  return await apiRequestsHelpers
    .getAddressBookAddresses(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetAirroadBooking2(
  params: openapi.JobsToAirroadListQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.JobsToAirroad2ListContentOpenapi> {
  const notificationTitle = 'Error loading AirRoad data';
  return await apiRequestsHelpers
    .getAirroadBooking2(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetAirroadJobs2(
  params: openapi.AirroadList2QueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.AirroadList2ContentOpenapi> {
  const notificationTitle = 'Error loading AirRoad data';
  return await apiRequestsHelpers
    .getAirroadJobs2(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchPostAirroadBookingSend(
  params: ReadonlyDeep<openapi.AirroadSingleCreateQueryOpenapi>,
  data: ReadonlyDeep<openapi.AirroadSingleCreateBodyOpenapi>,
  requestConfig: Readonly<AxiosRequestConfig> = {},
): Promise<openapi.AirroadSingleCreateContentOpenapi> {
  return await apiRequestsHelpers
    .postAirroadBookingSend(params, data, {
      ...validateCreatedStatusReqConf,
      ...requestConfig,
    })
    .then((res) => {
      showSuccessNotification({
        text: 'Job has been queued to book to Airroad',
      });
      return res.data;
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: 'Error when booking a job' });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchPostJobEditLock(
  params: {
    job_id: number;
    job_date: string;
  },
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.JobeditLockContentOpenapi> {
  try {
    const response = await apiRequestsHelpers.postJobeditLock(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    });

    return response.data;
  } catch (error) {
    if (!isCancel(error)) {
      if (isAxiosError(error)) {
        const errorMessage =
          getApiErrorText(error.response?.data) || 'Error locking job';

        switch (error.response?.status) {
          case 403: {
            const lockedBy = error.response.data?.locked_by || 'another user';
            showErrorNotification({
              title: 'Job is being edited by another user',
            });
            sendError(error);
            throw new KJobEditLockedByOtherError(
              'Job is being edited by another user',
              lockedBy,
            );
          }
          case 404:
            showErrorNotification({ title: 'Job not found' });
            sendError(error);
            throw new KJobEditLockNotFoundError('Job not found');
          case 409: {
            const lockedBy =
              getApiErrorText(error.response.data) || 'another system';
            showErrorNotification({ title: 'Job is locked by another system' });
            sendError(error);
            throw new KJobEditLockedByOtherError(
              'Job is locked by another system',
              lockedBy,
            );
          }
          default:
            showErrorNotification({ title: errorMessage });
            sendError(error);
            throw new KJobEditLockError(errorMessage);
        }
      }
    }
    throw error;
  }
}

export async function fetchPostJobEditUnlock(
  params: {
    job_id: number;
    job_date: string;
  },
  requestConfig: AxiosRequestConfig = {},
): Promise<void> {
  const notificationTitle = 'Error unlocking job';
  try {
    await apiRequestsHelpers.postJobeditUnlock(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    });
  } catch (error) {
    if (!isCancel(error)) {
      showErrorNotification({ title: notificationTitle });
      sendError(error);
    }
    throw error;
  }
}

export async function fetchPutJobEditJobDetails(
  params: openapi.JobeditCustomerDetailsQueryOpenapi,
  data: openapi.JobeditCustomerDetailsBodyOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<void> {
  try {
    await apiRequestsHelpers.putJobeditJobDetails(params, data, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    });

    showSuccessNotification({ title: 'Customer details updated successfully' });
  } catch (error) {
    if (!isCancel(error)) {
      if (isAxiosError(error)) {
        const errorMessage =
          getApiErrorText(error.response?.data) ||
          'Error updating customer details';

        switch (error.response?.status) {
          case 403: {
            const lockedBy = error.response.data?.locked_by || 'another user';
            showErrorNotification({
              title: 'Job is being edited by another user',
            });
            sendError(error);
            throw new KJobEditUpdateLockedError(
              'Job is being edited by another user',
              lockedBy,
            );
          }
          case 404:
            showErrorNotification({ title: 'Job not found' });
            sendError(error);
            throw new KJobEditUpdateNotFoundError('Job not found');
          case 409:
            throw new KJobEditUpdateUnlockedError('Job lock expired');
          default:
            showErrorNotification({ title: 'Failed to save customer details' });
            sendError(error);
            throw new KJobEditUpdateError(errorMessage);
        }
      }
    }
    throw error;
  }
}

export async function fetchPutJobEditProducts(
  params: openapi.JobeditProductsQueryOpenapi,
  data: openapi.JobeditProductsBodyOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.JobeditProductsContentOpenapi> {
  try {
    const response = await apiRequestsHelpers.putJobeditProducts(params, data, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    });

    showSuccessNotification({ title: 'Products updated successfully' });
    return response.data;
  } catch (error) {
    if (!isCancel(error)) {
      if (isAxiosError(error)) {
        const errorMessage =
          getApiErrorText(error.response?.data) || 'Error updating products';

        switch (error.response?.status) {
          case 403:
            showErrorNotification({
              title: 'Job is being edited by another user',
            });
            sendError(error);
            throw new KJobEditUpdateLockedError(
              'Job is being edited by another user',
            );
          case 404:
            showErrorNotification({ title: 'Job not found' });
            sendError(error);
            throw new KJobEditUpdateNotFoundError('Job not found');
          case 409:
            throw new KJobEditUpdateUnlockedError('Job lock expired');
          default:
            showErrorNotification({ title: errorMessage });
            sendError(error);
            throw new KJobEditUpdateError(errorMessage);
        }
      }
    }
    throw error;
  }
}

export async function fetchPutJobEditServiceCodes(
  params: openapi.JobeditServicesQueryOpenapi,
  data: openapi.JobeditServicesBodyOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.JobeditServicesContentOpenapi> {
  const notificationTitle = 'Error updating service codes';
  try {
    const response = await apiRequestsHelpers.putJobeditServices(params, data, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    });

    showSuccessNotification({ title: 'Service codes updated successfully' });
    return response.data;
  } catch (error) {
    if (!isCancel(error)) {
      showErrorNotification({ title: notificationTitle });
      sendError(error);
    }
    throw error;
  }
}

export async function fetchPutJobeditStopContentSection(
  params: openapi.PutJobeditStopContentSection_Query,
  data: openapi.PutJobeditStopContentSection_Body,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.PutJobeditStopContentSection_201> {
  try {
    const response = await apiRequestsHelpers.putJobeditStopContentSection(
      params,
      data,
      { ...validateCreatedStatusReqConf, ...requestConfig },
    );

    showSuccessNotification({
      title: 'Products & services updated successfully',
    });
    return response.data;
  } catch (error) {
    if (!isCancel(error)) {
      if (isAxiosError(error)) {
        const errorMessage =
          getApiErrorText(error.response?.data) ||
          'Error updating products & services';

        switch (error.response?.status) {
          case 403: {
            const lockedBy = error.response.data?.locked_by || 'another user';
            showErrorNotification({
              title: 'Job is being edited by another user',
            });
            sendError(error);
            throw new KJobEditUpdateLockedError(
              'Job is being edited by another user',
              lockedBy,
            );
          }
          case 404:
            showErrorNotification({ title: 'Job not found' });
            sendError(error);
            throw new KJobEditUpdateNotFoundError('Job not found');
          case 409:
            throw new KJobEditUpdateUnlockedError('Job lock expired');
          default:
            showErrorNotification({ title: errorMessage });
            sendError(error);
            throw new KJobEditUpdateError(errorMessage);
        }
      }
    }
    throw error;
  }
}

export async function fetchPostJobEditAddThirdStop(
  params: openapi.JobeditAddThirdStopQueryOpenapi,
  data: openapi.JobeditAddThirdStopBodyOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.JobeditAddThirdStopContentOpenapi> {
  const notificationTitle = 'Error adding third stop';
  try {
    const response = await apiRequestsHelpers.postJobeditStop(params, data, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    });

    showSuccessNotification({ title: 'Third stop added successfully' });
    return response.data;
  } catch (error) {
    if (!isCancel(error)) {
      showErrorNotification({ title: notificationTitle });
      sendError(error);
    }
    throw error;
  }
}

export async function fetchPutJobEditStopDetails(
  params: openapi.JobeditStopDetailsQueryOpenapi,
  data: openapi.JobeditStopDetailsBodyOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.JobeditStopDetailsContentOpenapi> {
  try {
    const response = await apiRequestsHelpers.putJobeditStop(params, data, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    });

    showSuccessNotification({ title: 'Stop details updated successfully' });
    return response.data;
  } catch (error) {
    if (!isCancel(error)) {
      if (isAxiosError(error)) {
        const errorMessage =
          getApiErrorText(error.response?.data) ||
          'Error updating stop details';

        switch (error.response?.status) {
          case 403:
            showErrorNotification({
              title: 'Job is being edited by another user',
            });
            sendError(error);
            throw new KJobEditUpdateLockedError(
              'Job is being edited by another user',
            );
          case 404:
            showErrorNotification({ title: 'Job not found' });
            sendError(error);
            throw new KJobEditUpdateNotFoundError('Job not found');
          case 409:
            throw new KJobEditUpdateUnlockedError('Job lock expired');
          default:
            showErrorNotification({ title: errorMessage });
            sendError(error);
            throw new KJobEditUpdateError(errorMessage);
        }
      }
    }
    throw error;
  }
}

export async function fetchPutJobEditStopSection(
  params: openapi.PutJobeditStopSection_Query,
  data: openapi.PutJobeditStopSection_Body,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.PutJobeditStopSection_201> {
  try {
    const response = await apiRequestsHelpers.putJobeditStopSection(
      params,
      data,
      { ...validateCreatedStatusReqConf, ...requestConfig },
    );

    showSuccessNotification({ title: 'Stops updated successfully' });
    return response.data;
  } catch (error) {
    if (!isCancel(error)) {
      if (isAxiosError(error)) {
        const errorMessage =
          getApiErrorText(error.response?.data) || 'Error updating stops';

        switch (error.response?.status) {
          case 403:
            showErrorNotification({
              title: 'Job is being edited by another user',
            });
            sendError(error);
            throw new KJobEditUpdateLockedError(
              'Job is being edited by another user',
            );
          case 404:
            showErrorNotification({ title: 'Job not found' });
            sendError(error);
            throw new KJobEditUpdateNotFoundError('Job not found');
          case 409:
            throw new KJobEditUpdateUnlockedError('Job lock expired');
          default:
            showErrorNotification({ title: errorMessage });
            sendError(error);
            throw new KJobEditUpdateError(errorMessage);
        }
      }
    }
    throw error;
  }
}

export async function fetchDeleteJobEditStop(
  params: openapi.JobeditStopDeleteQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<void> {
  const notificationTitle = 'Error deleting stop';
  try {
    await apiRequestsHelpers.deleteJobeditStop(params, {
      ...validateOKOrNoContentStatusReqConf,
      ...requestConfig,
    });

    showSuccessNotification({ title: 'Stop deleted successfully' });
  } catch (error) {
    if (!isCancel(error)) {
      showErrorNotification({ title: notificationTitle });
      sendError(error);
    }
    throw error;
  }
}

export async function fetchGetJobEditDetails(
  params: openapi.JobeditGetJobQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.JobeditGetJobContentOpenapi> {
  const notificationTitle = 'Error getting job details';
  try {
    const response = await apiRequestsHelpers.getJobeditJob(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    });

    return response.data;
  } catch (error) {
    if (!isCancel(error)) {
      showErrorNotification({ title: notificationTitle });
      sendError(error);
    }
    throw error;
  }
}

export async function fetchPostJobEditCancel(
  params: openapi.JobeditCancelQueryOpenapi,
  data: openapi.JobeditCancelBodyOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<void> {
  const notificationTitle = 'Error canceling job';
  try {
    await apiRequestsHelpers.postJobeditCancel(params, data, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    });

    showSuccessNotification({ title: 'Job canceled successfully' });
  } catch (error) {
    if (!isCancel(error)) {
      if (isAxiosError(error)) {
        const errorText = getApiErrorText(error.response?.data);
        showErrorNotification({ title: errorText || notificationTitle });
        sendError(error);
      } else {
        showErrorNotification({ title: notificationTitle });
      }
    }
    throw error;
  }
}

export async function fetchPostJobEditFutile(
  params: openapi.JobeditFutileQueryOpenapi,
  data: openapi.JobeditFutileBodyOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<void> {
  const notificationTitle = 'Error marking job as futile';
  try {
    await apiRequestsHelpers.postJobeditFutile(params, data, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    });

    showSuccessNotification({ title: 'Job marked as futile successfully' });
  } catch (error) {
    if (!isCancel(error)) {
      if (isAxiosError(error)) {
        const errorText = getApiErrorText(error.response?.data);
        showErrorNotification({ title: errorText || notificationTitle });
        sendError(error);
      } else {
        showErrorNotification({ title: notificationTitle });
      }
    }
    throw error;
  }
}

export async function fetchGetJobLocation(
  params: {
    job_id: number;
    job_date: string;
  },
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.JobLocationsContentOpenapi> {
  const notificationTitle = 'Failed to get Job location';
  return await apiRequestsHelpers
    .getJobLocation(
      {
        job_id: params.job_id,
        job_date: params.job_date,
      },
      { ...validateOKStatusReqConf, ...requestConfig },
    )
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetDespatchJobs(
  params: openapi.DespatchJobsQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.DespatchJobsContentOpenapi> {
  const notificationTitle = 'Error getting jobs';
  return await apiRequestsHelpers
    .getDespatchJobs(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchGetSmsDaily(
  params: openapi.SMSDailyQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.SMSDailyContentOpenapi> {
  const notificationTitle = 'Error getting SMS Daily data';
  const formattedParams = {
    ...params,
    date: params.date ? format(params.date, 'yyyy-MM-dd') : params.date,
  };
  return await apiRequestsHelpers
    .getSmsDaily(formattedParams, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (isCancel(err)) throw err;

      showErrorNotification({ title: notificationTitle });
      sendError(err);
      throw err;
    });
}

export async function fetchPostSmsDaily(
  data: openapi.SMSSendRequestOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.SMSSendResponseOpenapi> {
  const notificationTitle = 'SMS sending failed';
  return await apiRequestsHelpers
    .postSmsDaily(data, requestConfig)
    .then((res) => {
      const count = res.data.count || data.items.length;
      showSuccessNotification({
        title: 'SMS sent successfully',
        text: `${count} message${count !== 1 ? 's' : ''} sent to selected drivers`,
      });
      return res.data;
    })
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({
          title: notificationTitle,
          text: 'An error occurred while sending SMS messages. Please try again.',
        });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchSMSHistoryData(
  params: openapi.SMSHistoryQueryOpenapi,
  requestConfig: AxiosRequestConfig = {},
): Promise<openapi.SMSHistoryContentOpenapi> {
  const notificationTitle = 'Error getting SMS History data';
  return await apiRequestsHelpers
    .getSMSHistoryData(params, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      if (!isCancel(err)) {
        showErrorNotification({ title: notificationTitle });
        sendError(err);
      }
      throw err;
    });
}

export async function fetchPostDespatchJobUndoAll(
  params: openapi.PostDespatchJobUndoAll_Query,
  data?: openapi.PostDespatchJobUndoAll_Body,
  requestConfig: AxiosRequestConfig = {},
): Promise<void> {
  const notificationTitle = 'Error to undo all recent changes';
  try {
    await apiRequestsHelpers.postDespatchJobUndoAll(params, data, {
      ...validateOKStatusReqConf,
      ...requestConfig,
    });

    showSuccessNotification({
      title: 'Undone all recent job changes successfully',
    });
  } catch (error) {
    if (!isCancel(error)) {
      showErrorNotification({ title: notificationTitle });
      sendError(error);
    }
    throw error;
  }
}

export async function fetchPostPacJobBookApi(
  data: PacJobBookApiBody,
  requestConfig: AxiosRequestConfig = {},
): Promise<{ jobId: number; jobDate: string }> {
  return await postJobBookingApi(
    apiRequestsHelpers.postPacJobBookApi(data, {
      ...validateCreatedStatusReqConf,
      ...requestConfig,
    }),
    'PAC job booking failed',
  );
}

export async function fetchPostBookBoxesJobBookApi(
  data: BookBoxesJobBookApiBody,
  requestConfig: AxiosRequestConfig = {},
): Promise<{ jobId: number; jobDate: string }> {
  return await postJobBookingApi(
    apiRequestsHelpers.postBookBoxesJobBookApi(data, {
      ...validateCreatedStatusReqConf,
      ...requestConfig,
    }),
    'Book boxes job booking failed',
  );
}
